import { Component, Ref, Prop } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { cart } from '@/theme/Cart';
import { disableBodyScrolling } from '@/utils/disableBodyScrolling';

@Component
export class ProductCardQuickAdd extends ThemeComponent {

    @Prop({default: window.scrollY}) scrollPosition!: number;

    @Ref() closeButton!: HTMLButtonElement;
    @Ref() quickAddContainer!: HTMLDivElement;

    private keyboardListener: ((event: KeyboardEvent) => void) | null = null;
    private previouslyFocusedElement: HTMLElement | null = null;

    init() {

        this.scrollPosition = window.scrollY;

        this.setCloseButtonListener();
        this.addOutsideClickListener();
        this.addKeyboardListener();
        this.addOpenListener();

    }

    /**
     * Listen for close button clicks
    */
    setCloseButtonListener() {
        // Listen for click on the quick add button
        if (this.closeButton) {
            this.$listen(this.closeButton, 'click', (e) => {
                this.handleButton();
            });
        }
    }

    /**
     * Handle quick add state
    */
    handleButton() {
        this.classList.remove('is-active');
        this.scrollPosition = disableBodyScrolling(false, this.scrollPosition);
        this.restoreFocusAfterClose();
    }

    addOutsideClickListener() {
        document.addEventListener('click', (event) => {
            const target = event.target as HTMLElement;
            if (this.quickAddContainer && !this.quickAddContainer.contains(target)) {
                this.handleButton();
            }
        });
    }

    /**
     * Add keyboard event listener for Escape key
    */
    addKeyboardListener() {
        this.keyboardListener = (event: KeyboardEvent) => {
            const isActive = this.classList.contains('is-active');

            // Close on Escape when active
            if (event.key === 'Escape' && isActive) {
                this.handleButton();
                return;
            }

            // Trap focus with Tab when active
            if (event.key === 'Tab' && isActive) {
                const focusable = this.getFocusableElements();
                if (focusable.length === 0) {return;}

                const first = focusable[0] as HTMLElement;
                const last = focusable[focusable.length - 1] as HTMLElement;
                const active = document.activeElement as HTMLElement | null;

                if (event.shiftKey) {
                    if (active === first || !active || !this.contains(active)) {
                        event.preventDefault();
                        last.focus();
                    }
                } else {
                    if (active === last || !this.contains(active)) {
                        event.preventDefault();
                        first.focus();
                    }
                }
            }
        };

        document.addEventListener('keydown', this.keyboardListener);
    }

    /**
     * Clean up event listeners when component is destroyed
    */
    destroy() {
        if (this.keyboardListener) {
            document.removeEventListener('keydown', this.keyboardListener);
        }
    }

    /**
     * Listen for open event to set initial focus and store opener
     */
    private addOpenListener() {
        // Use native addEventListener for custom events
        this.addEventListener('quickAdd:open', () => {
            this.previouslyFocusedElement = document.activeElement as HTMLElement;

            const focusable = this.getFocusableElements();
            if (focusable.length > 0) {
                const first = focusable[0] as HTMLElement;
                setTimeout(() => first.focus(), 0);
            } else if (this.quickAddContainer) {
                this.quickAddContainer.setAttribute('tabindex', '-1');
                this.quickAddContainer.focus();
            }
        });
    }

    /**
     * Return focus to previously focused element when closing
     */
    private restoreFocusAfterClose() {
        if (this.previouslyFocusedElement) {
            this.previouslyFocusedElement.focus();
            this.previouslyFocusedElement = null;
        }
    }

    /**
     * Get focusable elements inside the quick add container
     */
    private getFocusableElements(): HTMLElement[] {
        const selectors = [
            'a[href]',
            'area[href]',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'button:not([disabled])',
            'iframe',
            'object',
            'embed',
            '[contenteditable]',
            '[tabindex]:not([tabindex="-1"])',
        ];

        const root = this.quickAddContainer ?? this;
        return Array.from(root.querySelectorAll<HTMLElement>(selectors.join(',')))
            .filter((el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length));
    }

}
